import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";
import { useFuzzySearch } from "./useFuzzySearch";

export interface UseTileReplaceOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

const extractReplacementTexts = (
  tile: ReviewTileItem
): { findText: string; replaceText: string } | null => {
  const originalData = tile.originalData;
  if (!originalData) return null;

  let findText: string | undefined;
  let replaceText: string | undefined;

  if (
    tile.originalKey === "ambiguities" &&
    originalData.ambiguousText &&
    originalData.suggestedclause
  ) {
    findText = originalData.ambiguousText;
    replaceText = originalData.suggestedclause;
  } else if (
    tile.originalKey === "complexLanguage" &&
    originalData.complexText &&
    originalData.suggestions
  ) {
    findText = originalData.complexText;
    replaceText = originalData.suggestions;
  } else if (
    tile.originalKey === "inconsistencies" &&
    originalData.text1 &&
    originalData.suggestion
  ) {
    findText = originalData.text1;
    replaceText = originalData.suggestion;
  }
  // Add more cases if needed for other tile types

  if (findText && replaceText) {
    return { findText, replaceText };
  }

  return null;
};

export const useTileReplace = (options?: UseTileReplaceOptions) => {
  const [isReplacing, setIsReplacing] = useState(false);
  const [replacingTileId, setReplacingTileId] = useState<string | null>(null);
  const { fuzzySearchInDocument } = useFuzzySearch();

  const replaceSingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    setIsReplacing(true);
    setReplacingTileId(tile.id);

    const replacementTexts = extractReplacementTexts(tile);
    if (!replacementTexts) {
      options?.onError?.("This tile is not replaceable.");
      setIsReplacing(false);
      setReplacingTileId(null);
      return false;
    }

    const { findText, replaceText } = replacementTexts;
    // Truncate findText to 255 characters to avoid SearchStringInvalidOrTooLong error
    const truncatedFindText = findText.substring(0, 255);

    try {
      await Word.run(async (context) => {
        const searchOptions = { matchCase: false, matchWholeWord: false };
        const searchResults = context.document.body.search(truncatedFindText, searchOptions);
        searchResults.load("items");
        await context.sync();

        if (searchResults.items.length > 0) {
          // Replace all occurrences.
          searchResults.items.forEach((item) => {
            item.insertText(replaceText, Word.InsertLocation.replace);
          });
          await context.sync();
          options?.onSuccess?.(`Replaced ${searchResults.items.length} instance(s).`);
        } else {
          // Fallback to fuzzy search
          const fuzzyMatchFound = await fuzzySearchInDocument(truncatedFindText);

          if (fuzzyMatchFound) {
            // If fuzzy search found and selected the text, we need to replace the selection
            await Word.run(async (context) => {
              const selection = context.document.getSelection();
              selection.insertText(replaceText, Word.InsertLocation.replace);
              await context.sync();
            });
            options?.onSuccess?.("Replaced 1 instance using fuzzy search.");
          } else {
            options?.onError?.(`Could not find the text to replace.`);
          }
        }
      });
      return true;
    } catch (error: any) {
      console.error("Error replacing text:", error);
      options?.onError?.(`Failed to replace text: ${error.message}`);
      return false;
    } finally {
      setIsReplacing(false);
      setReplacingTileId(null);
    }
  };

  return {
    isReplacing,
    replacingTileId,
    replaceSingleTile,
  };
};
